import { DotsThree, User, UserMinus, X } from '@phosphor-icons/react';
import type { InviteDto, MemberDto } from '@reelplicate/api-client/model';
import {
  Avatar,
  IconButton,
  Inline,
  Link,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Select,
  SelectItem,
  Stack,
} from '@reelplicate/ui';

import { memberName } from './names';
import { fromApiRole, type Role, roleName, ROLES } from './roles';

const DAY = 24 * 60 * 60 * 1000;
const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "today", "2 days ago", "in 5 days" - invites live for a week, so days will do. */
const relativeDays = (date: string) =>
  RELATIVE.format(Math.round((new Date(date).getTime() - Date.now()) / DAY), 'day');

/**
 * Figma "Member Row" (107:86): 56px, 12 / 8px sides - a 32px Avatar, name
 * (+ "(you)") over the email, then the role: a 120px Select and a More menu
 * (Remove) for owners managing someone else, plain text otherwise. You can't
 * change or remove yourself here.
 */
export function MemberRow({
  member,
  canManage,
  onRoleChange,
  onRemove,
}: {
  member: MemberDto;
  /** The signed-in user is an owner. */
  canManage: boolean;
  onRoleChange: (role: Role) => void;
  onRemove: () => void;
}) {
  const name = memberName(member);
  const role = fromApiRole(member.role);
  const editable = canManage && !member.isYou;
  return (
    <Inline as="li" gap={3} className="h-14 rounded-md pr-2 pl-3">
      <Avatar size={32} name={name} src={member.imageUrl ?? undefined} alt="" />
      <Stack gap={0} className="min-w-0 flex-1">
        <Inline gap={1.5}>
          <span className="text-body-md-strong text-text-primary truncate">{name}</span>
          {member.isYou && <span className="text-caption text-text-tertiary">(you)</span>}
        </Inline>
        {member.email && (
          <span className="text-caption text-text-tertiary truncate">{member.email}</span>
        )}
      </Stack>
      {editable ? (
        <>
          <Select
            size="sm"
            className="w-30"
            value={role}
            onValueChange={(value) => onRoleChange(value as Role)}
            aria-label={`Role for ${name}`}
          >
            {ROLES.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </Select>
          <Menu>
            <MenuTrigger asChild>
              <IconButton
                icon={DotsThree}
                size="sm"
                variant="ghost"
                aria-label={`More for ${name}`}
              />
            </MenuTrigger>
            <MenuContent align="end">
              <MenuItem type="danger" leadingIcon={UserMinus} onSelect={onRemove}>
                Remove from workspace
              </MenuItem>
            </MenuContent>
          </Menu>
        </>
      ) : (
        <>
          <span className="text-body-sm text-text-secondary">{roleName(role)}</span>
          {/* Keeps the role lined up with the rows that have a More button. */}
          {canManage && <span aria-hidden className="size-8 shrink-0" />}
        </>
      )}
    </Inline>
  );
}

/**
 * Figma "Member Row", pending invite (328:2280): an icon Avatar, the email
 * over who invited them and when, then - for owners - Resend and a Revoke
 * icon button.
 */
export function InviteRow({
  invite,
  canManage,
  onResend,
  onRevoke,
}: {
  invite: InviteDto;
  canManage: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  const invitedBy = invite.invitedByName ? ` by ${invite.invitedByName}` : '';
  return (
    <Inline as="li" gap={3} className="h-14 rounded-md pr-2 pl-3">
      <Avatar icon={User} size={32} alt="" />
      <Stack gap={0} className="min-w-0 flex-1">
        <span className="text-body-md-strong text-text-primary truncate">
          {invite.email}
        </span>
        <span className="text-caption text-text-tertiary truncate">
          Invited as {roleName(fromApiRole(invite.role))}
          {invitedBy} · sent {relativeDays(invite.lastSentAt)} · expires{' '}
          {relativeDays(invite.expiresAt)}
        </span>
      </Stack>
      {canManage && (
        <>
          <Link asChild size="sm">
            <button type="button" onClick={onResend}>
              Resend
            </button>
          </Link>
          <IconButton
            icon={X}
            size="sm"
            variant="ghost"
            aria-label={`Revoke invite for ${invite.email}`}
            onClick={onRevoke}
          />
        </>
      )}
    </Inline>
  );
}
